import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/useAuthStore';
import { applySeo } from '@/functions/seo';

import LoginPage from '@/pages/LoginPage.vue';
import RegisterPage from '@/pages/RegisterPage.vue';
import AuthCallbackPage from '@/pages/AuthCallbackPage.vue';
import GoogleCompletePage from '@/pages/GoogleCompletePage.vue';
import PartnerConsentPage from '@/pages/PartnerConsentPage.vue';
import ProfilePage from '@/pages/ProfilePage.vue';
import AdminUsers from '@/pages/admin/AdminUsers.vue';
import AdminRoles from '@/pages/admin/AdminRoles.vue';
import AdminClubs from '@/pages/admin/AdminClubs.vue';
import AdminComites from '@/pages/admin/AdminComites.vue';
import AdminLigues from '@/pages/admin/AdminLigues.vue';
import AdminOrgHistory from '@/pages/admin/AdminOrgHistory.vue';
import AdminLicenses from '@/pages/admin/AdminLicenses.vue';
import AdminCleanup from '@/pages/admin/AdminCleanup.vue';
import AdminMonitor from '@/pages/admin/AdminMonitor.vue';
import AdminAffiliations from '@/pages/admin/AdminAffiliations.vue';
import AdminAccessRequests from '@/pages/admin/AdminAccessRequests.vue';
import AdminPartners from '@/pages/admin/AdminPartners.vue';
import AdminEventsPremium from '@/pages/admin/AdminEventsPremium.vue';
import Page404 from '@/pages/Page404.vue';
import ContactPage from '@/pages/ContactPage.vue';
import MentionsLegales from '@/pages/MentionsLegales.vue';
import PolitiqueConfidentialite from '@/pages/PolitiqueConfidentialite.vue';
import PartnerIntegrationPage from '@/pages/PartnerIntegrationPage.vue';
import LandingPage from '@/pages/LandingPage.vue';
import HistoryPage from '@/pages/HistoryPage.vue';
import AdminHistory from '@/pages/admin/AdminHistory.vue';

const routes = [
  { path: '/', name: 'Home', component: LandingPage },
  { path: '/login', name: 'Login', component: LoginPage, meta: { title: 'Connexion', description: 'Connectez-vous à votre compte DartsHub pour accéder à Tournois DartsHub et à tous les sites fléchettes partenaires.' } },
  { path: '/register', name: 'Register', component: RegisterPage, meta: { title: 'Créer un compte', description: 'Créez gratuitement votre compte DartsHub : un seul identifiant pour vous inscrire aux tournois de fléchettes et accéder aux sites partenaires.' } },
  { path: '/auth/callback', name: 'AuthCallback', component: AuthCallbackPage, meta: { noindex: true } },
  { path: '/auth/google/complete', name: 'GoogleComplete', component: GoogleCompletePage, meta: { noindex: true } },
  { path: '/partners/consent', name: 'PartnerConsent', component: PartnerConsentPage, meta: { noindex: true } },
  { path: '/histoire', name: 'Histoire', component: HistoryPage, meta: { title: 'Histoire des fléchettes en France', description: "Fédérations, Ligues, Comités et clubs de fléchettes en France depuis 1922 : frise, carte, doyens et palmarès des championnats de France.", image: 'https://dartshub.fr/histoire-banner.png' } },
  { path: '/contact', name: 'Contact', component: ContactPage, meta: { title: 'Contact', description: "Une question sur DartsHub, Tournois DartsHub ou la gestion de vos tournois de fléchettes ? Contactez l'équipe DartsHub." } },
  { path: '/mentions-legales', name: 'MentionsLegales', component: MentionsLegales, meta: { title: 'Mentions légales' } },
  { path: '/politique-confidentialite', name: 'PolitiqueConfidentialite', component: PolitiqueConfidentialite, meta: { title: 'Politique de confidentialité' } },
  { path: '/partenaires/integration', name: 'PartnerIntegration', component: PartnerIntegrationPage, meta: { title: 'Intégration partenaires', description: 'Intégrez la connexion DartsHub à votre site ou application fléchettes : un compte unique pour vos joueurs.' } },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfilePage,
    meta: { requiresAuth: true },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/roles',
    name: 'AdminRoles',
    component: AdminRoles,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/clubs',
    name: 'AdminClubs',
    component: AdminClubs,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/comites',
    name: 'AdminComites',
    component: AdminComites,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/ligues',
    name: 'AdminLigues',
    component: AdminLigues,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/history',
    name: 'AdminHistory',
    component: AdminHistory,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/org-history',
    name: 'AdminOrgHistory',
    component: AdminOrgHistory,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/licenses',
    name: 'AdminLicenses',
    component: AdminLicenses,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/cleanup',
    name: 'AdminCleanup',
    component: AdminCleanup,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/monitor',
    name: 'AdminMonitor',
    component: AdminMonitor,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/affiliations',
    name: 'AdminAffiliations',
    component: AdminAffiliations,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/access-requests',
    name: 'AdminAccessRequests',
    component: AdminAccessRequests,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/events-premium',
    name: 'AdminEventsPremium',
    component: AdminEventsPremium,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/partners',
    name: 'AdminPartners',
    component: AdminPartners,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  { path: '/:pathMatch(.*)*', name: 'NotFound', component: Page404, meta: { title: 'Page introuvable', noindex: true } },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const authStore = useAuthStore();
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }
  if (to.meta.requiresAdmin && !authStore.isSuperAdmin) {
    return { name: 'Profile' };
  }
});

// Pages privées (requiresAuth/requiresAdmin) jamais indexées.
router.afterEach((to) => {
  applySeo({
    title: to.meta.title as string | undefined,
    description: to.meta.description as string | undefined,
    path: to.path,
    noindex: Boolean(to.meta.noindex || to.meta.requiresAuth),
    image: to.meta.image as string | undefined,
  });
});

export default router;
